/**
 * CameraPopup
 *
 * Popup component that displays camera info with live thumbnail when
 * clicking a camera marker on the dispatch map.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  Skeleton,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SendIcon from '@mui/icons-material/Send';
import styles from './CameraPopup.module.css';

export interface CameraPopupCamera {
  id: number;
  name: string;
  display_name?: string;
  site_id: number;
  site_name: string;
  status: 'online' | 'offline' | 'unknown';
  stream_type: 'hls' | 'jpeg' | 'mjpeg';
  alert_count?: number;
}

interface CameraPopupProps {
  camera: CameraPopupCamera;
  onClose: () => void;
  onViewStream: (camera: CameraPopupCamera) => void;
  onViewAlerts?: (camera: CameraPopupCamera) => void;
  /** Callback when "Dispatch" is clicked. Undefined = button disabled (no customer/address linked). */
  onDispatch?: () => void;
}

/** Snapshot refresh interval in milliseconds */
const SNAPSHOT_REFRESH_INTERVAL = 5000;

/**
 * Build the snapshot URL for a camera
 */
const getSnapshotUrl = (siteId: number, cameraId: number, timestamp: number): string => {
  const baseURL = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:10000'
    : '';
  return `${baseURL}/api3.2/canvms/sites/${siteId}/cameras/${cameraId}/proxy/snapshot?t=${timestamp}`;
};

/**
 * Renders a popup with camera information and live thumbnail preview.
 */
const CameraPopup: React.FC<CameraPopupProps> = ({
  camera,
  onClose,
  onViewStream,
  onViewAlerts,
  onDispatch,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [timestamp, setTimestamp] = useState(Date.now());

  const isOnline = camera.status === 'online';
  const supportsSnapshot = camera.stream_type === 'jpeg' || camera.stream_type === 'mjpeg';
  const hasAlerts = (camera.alert_count ?? 0) > 0;
  const displayName = camera.display_name || camera.name;

  // Refresh thumbnail every 5 seconds
  useEffect(() => {
    if (!isOnline) return;

    const interval = setInterval(() => {
      setTimestamp(Date.now());
    }, SNAPSHOT_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [isOnline]);

  // Reset image state when camera changes
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
    setTimestamp(Date.now());
  }, [camera.id]);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    setImageError(false);
  }, []);

  const handleImageError = useCallback(() => {
    setImageLoaded(true);
    setImageError(true);
  }, []);

  const handleViewStream = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewStream(camera);
  };

  const handleViewAlerts = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewAlerts?.(camera);
  };

  const handleDispatch = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDispatch?.();
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  const statusColor = {
    online: '#4CAF50',
    offline: '#9E9E9E',
    unknown: '#FFC107',
  }[camera.status];

  const statusLabel = {
    online: 'Online',
    offline: 'Offline',
    unknown: 'Unknown',
  }[camera.status];

  return (
    <Paper className={styles.popup} elevation={4}>
      {/* Header */}
      <Box className={styles.header}>
        <Box className={styles.headerContent}>
          <Typography variant="subtitle1" className={styles.cameraName}>
            {displayName}
          </Typography>
          <Typography variant="body2" color="textSecondary" className={styles.siteName}>
            Site: {camera.site_name}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          className={styles.closeButton}
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Thumbnail */}
      <Box className={styles.thumbnailContainer}>
        {isOnline && !imageLoaded && !imageError && (
          <Skeleton
            variant="rectangular"
            className={styles.thumbnailSkeleton}
            animation="wave"
          />
        )}

        {isOnline && !imageError && (
          <img
            src={getSnapshotUrl(camera.site_id, camera.id, timestamp)}
            alt={`${displayName} snapshot`}
            className={styles.thumbnail}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ display: imageLoaded ? 'block' : 'none' }}
          />
        )}

        {(imageError || !isOnline) && (
          <Box className={styles.thumbnailPlaceholder}>
            <VideocamOffIcon className={styles.placeholderIcon} />
            <Typography variant="caption" color="textSecondary">
              {!isOnline ? 'Camera offline' : 'Unable to load snapshot'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Status */}
      <Box className={styles.statusRow}>
        <Typography variant="body2" color="textSecondary">
          Status:
        </Typography>
        <Box className={styles.statusValue}>
          <Box
            className={styles.statusDot}
            style={{ backgroundColor: statusColor }}
          />
          <Typography variant="body2" style={{ color: statusColor, fontWeight: 500 }}>
            {statusLabel}
          </Typography>
        </Box>
      </Box>

      {/* Actions */}
      <Box className={styles.actions}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<VideocamIcon />}
          onClick={handleViewStream}
          disabled={!isOnline}
          className={styles.actionButton}
        >
          View Stream
        </Button>

        {hasAlerts && onViewAlerts && (
          <Button
            variant="outlined"
            size="small"
            color="warning"
            startIcon={<NotificationsActiveIcon />}
            onClick={handleViewAlerts}
            className={styles.actionButton}
          >
            View Alerts ({camera.alert_count})
          </Button>
        )}

        <Tooltip
          title={onDispatch ? 'Open scheduler to create a patrol' : 'Camera has no linked customer/address'}
          arrow
        >
          <span>
            <Button
              variant="contained"
              size="small"
              color="primary"
              startIcon={<SendIcon />}
              onClick={handleDispatch}
              disabled={!onDispatch}
              className={styles.actionButton}
            >
              Dispatch
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Paper>
  );
};

export default CameraPopup;
